// src/components/card-info-modal/CardInfoModal.js
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import {
  ModalContainer,
  ModalHeader,
  CloseButton,
  ModalBody,
  InfoLabelContainer,
  ContentRow,
  LeftContent,
  RightContent,
  DescriptionPreview,
  CopyConfirmation,
  CopyBtnContainer
} from './CardInfoModal.styles.js';

// assets
import copyIcon from '../../../../../../../../assets/system/icons/copy-icon.png';
// Components
import HeaderDetails from './components/header-details/HeaderDetails.js';
import ParallelPanels from './components/parallel-panels/ParallelPanels.js';
import InputBar from '../../../../../../../../components/input-bar/InputBar.js';
import InputTextfield from '../../../../../../../../components/input-textfield/InputTextfield.js';
import { normalizeForCompare } from '../../../../../../../../utils/textNormalization';
import { buildEbayTitle, buildEbayDescription } from '../../../../../../../../utils/ebay/ebayListingText';

export default function CardInfoModal({
  isOpen,
  onClose,
  card,
  series,
  buildEbaySearchUrl,
  parallels = [],
  dispatchCardToQueue,
  handleRemoveCard,
  handleAddToWhatnot,
  handleAddToEbay,
  handleAddToDiscord,
  handleAddToShowcase,
  showcaseView = false
}) {
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [copiedItem, setCopiedItem] = useState(null); // 'title' or 'description' or null
  const [variationQtyValues, setVariationQtyValues] = useState({});
  const [variationSerialValues, setVariationSerialValues] = useState({});

  // Initialize selected variation to first one when modal opens or card changes
  useEffect(() => {
    if (card?.collectedCards && card.collectedCards.length > 0) {
      setSelectedVariation(card.collectedCards[0]);
    } else {
      setSelectedVariation(null);
    }
    // Initialize qty values; serial-numbered collected cards default to -1 (REMOVE mode)
    if (card?.collectedCards) {
      const initialQtyValues = {};
      card.collectedCards.forEach(variation => {
        const isSerial = (variation.cardSupply ?? variation.limitedTo) != null;
        initialQtyValues[normalizeForCompare(variation.cardParallelName)] = isSerial ? -1 : 1;
      });
      setVariationQtyValues(initialQtyValues);
    }
    setVariationSerialValues({});
  }, [card?.collectedCards, isOpen]);

  // Clear copy confirmation after 3 seconds
  useEffect(() => {
    if (copiedItem) {
      const timer = setTimeout(() => {
        setCopiedItem(null);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [copiedItem]);

  // Helper to create card data for a variation
  const createCardDataForVariation = useCallback((variation) => {
    if (!card) return null;
    // Find the parallel object from parallels array to get the color and odds
    const parallelObj = parallels.find(p => normalizeForCompare(p.name) === normalizeForCompare(variation.cardParallelName));
    const cardColor = parallelObj?.color || variation.cardColor || '#FFFFFF';

    return {
      ...card,
      cardAttributes: [...(card.cardAttributes || [])],
      collectedCards: [{
        cardParallelName: variation.cardParallelName,
        cardColor: cardColor,
        cardQty: 1,
        cardPrintNumber: variation.cardPrintNumber ?? null,
        cardSupply: variation.cardSupply ?? null,
        cardOdds: parallelObj?.odds ?? null
      }]
    };
  }, [card, parallels]);

  // Handle quantity change for a specific variation
  const handleVariationQtyChange = useCallback((variationName, delta) => {
    const key = normalizeForCompare(variationName);
    setVariationQtyValues(prev => {
      const currentQty = prev[key] || 1;
      const newValue = currentQty + delta;

      // Check if this is an uncollected variation (not in collectedCards)
      const isUncollected = !card?.collectedCards?.some(c => normalizeForCompare(c.cardParallelName) === key);

      // For uncollected variations, don't allow negative values
      if (isUncollected && newValue < 1) {
        return { ...prev, [key]: 1 };
      }

      // Skip 0: go directly from 1 to -1 or -1 to 1
      if (newValue === 0) {
        return { ...prev, [key]: delta > 0 ? 1 : -1 };
      }

      // Limit negative values to max removable quantity (only for collected variations)
      if (newValue < 0) {
        const variation = card?.collectedCards?.find(c => normalizeForCompare(c.cardParallelName) === key);
        const maxRemovable = variation?.cardQty || 0;
        const absNewValue = Math.abs(newValue);
        if (absNewValue > maxRemovable) {
          return { ...prev, [key]: maxRemovable > 0 ? -maxRemovable : -1 };
        }
      }

      return { ...prev, [key]: newValue };
    });
  }, [card?.collectedCards]);

  const handleVariationSerialChange = useCallback((variationName, value) => {
    setVariationSerialValues(prev => ({
      ...prev,
      [normalizeForCompare(variationName)]: value,
    }));
  }, []);

  // Handle add/remove for a specific variation
  const handleVariationAddOrRemove = useCallback((variation) => {
    if (!dispatchCardToQueue || showcaseView) return;

    const key = normalizeForCompare(variation.cardParallelName);
    const qtyValue = variationQtyValues[key] ?? 1;
    if (qtyValue === 0) return;

    const supply = variation.cardSupply ?? variation.limitedTo ?? null;
    const isSerial = supply != null;

    if (qtyValue > 0) {
      let variationToAdd = variation;
      if (isSerial) {
        const serialNum = parseInt(variationSerialValues[key] ?? '', 10);
        if (isNaN(serialNum) || serialNum < 1 || serialNum > supply) return;
        variationToAdd = { ...variation, cardPrintNumber: serialNum };
      }
      const cardData = createCardDataForVariation(variationToAdd);
      if (cardData) {
        dispatchCardToQueue(cardData, 1);
      }
    } else {
      // Remove card — for serial cards pass the full variation so Firestore can match by print number
      if (!handleRemoveCard || !card) return;
      const parallelIdentifier = isSerial ? variation : variation.cardParallelName;
      handleRemoveCard(card, parallelIdentifier, 1);
    }

    // Reset qty and serial input after action
    const isSerial2 = supply != null;
    setVariationQtyValues(prev => ({ ...prev, [key]: isSerial2 ? -1 : 1 }));
    if (isSerial) {
      setVariationSerialValues(prev => ({ ...prev, [key]: '' }));
    }
  }, [variationQtyValues, variationSerialValues, dispatchCardToQueue, handleRemoveCard, showcaseView, card, createCardDataForVariation]);

  if (!isOpen || !card) return null;

  const { collectedCards } = card;

  // Calculate uncollected variations (normalized so "Base" / "BASE" match)
  const collectedVariationNamesNorm = new Set(
    (collectedCards || []).map(c => normalizeForCompare(c.cardParallelName))
  );
  const uncollectedVariations = (parallels || []).filter(
    parallel => !collectedVariationNamesNorm.has(normalizeForCompare(parallel.name))
  );

  const handleEbayClick = (e, parallel) => {
    e?.stopPropagation?.();
    if (buildEbaySearchUrl && card && parallel) {
      try {
        const ebayUrl = buildEbaySearchUrl(card, parallel);
        if (ebayUrl) {
          window.open(ebayUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        // Error building eBay URL - fail silently
      }
    }
  };

  const handleCopyDescription = (parallel = null) => {
    const description = buildEbayDescription(card, series, parallel);
    navigator.clipboard.writeText(description).then(() => {
      setCopiedItem('description');
    }).catch(() => {});
  };

  const handleCopyTitle = (parallel = null) => {
    const title = buildEbayTitle(card, series, parallel);
    navigator.clipboard.writeText(title).then(() => {
      setCopiedItem('title');
    }).catch(() => {});
  };

  const modalContent = (
    <div onClick={onClose} className='modal-overlay'>
      <ModalContainer onClick={(e) => e.stopPropagation()} className='modal-container'>
        <ModalHeader>
          <HeaderDetails card={card} />
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContentRow>
          {((collectedCards && collectedCards.length > 0) || uncollectedVariations.length > 0) && (
              <RightContent>
                <ParallelPanels
                  collectedCards={collectedCards || []}
                  uncollectedVariations={uncollectedVariations}
                  selectedVariation={selectedVariation}
                  onVariationSelect={setSelectedVariation}
                  variationQtyValues={variationQtyValues}
                  onVariationQtyChange={handleVariationQtyChange}
                  onVariationAddOrRemove={handleVariationAddOrRemove}
                  onEbayClick={handleEbayClick}
                  showcaseView={showcaseView}
                  dispatchCardToQueue={dispatchCardToQueue}
                  handleRemoveCard={handleRemoveCard}
                  onAddToWhatnot={handleAddToWhatnot ? (variation) => handleAddToWhatnot(card, variation) : undefined}
                  onAddToEbay={handleAddToEbay ? (variation) => handleAddToEbay(card, variation) : undefined}
                  onAddToDiscord={handleAddToDiscord ? (variation) => handleAddToDiscord(card, variation) : undefined}
                  onAddToShowcase={handleAddToShowcase ? (variation) => handleAddToShowcase(card, variation) : undefined}
                  variationSerialValues={variationSerialValues}
                  onVariationSerialChange={handleVariationSerialChange}
                />
              </RightContent>
            )}
            <LeftContent>
              {collectedCards && collectedCards.length > 0 && (
                <DescriptionPreview>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <InfoLabelContainer>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <InputBar
                          id="card-info-ebay-title"
                          label="eBay Listing Title"
                          value={buildEbayTitle(card, series, selectedVariation)}
                          updateValue={() => {}}
                          readOnly
                          onClick={(e) => e.target.select()}
                          expands={true}
                        />
                      </div>
                      <CopyBtnContainer
                        onClick={() => {
                          handleCopyTitle(selectedVariation);
                        }}
                      >
                        {copiedItem === 'title' ? (
                          <CopyConfirmation>COPIED TO CLIPBOARD!</CopyConfirmation>
                        ) : (
                          <>
                            <img
                              src={copyIcon}
                              alt="Copy"
                              style={{ width: '12px', height: '12px' }}
                            />
                            <span style={{ fontSize: '11px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                              Copy
                            </span>
                          </>
                        )}
                      </CopyBtnContainer>
                    </InfoLabelContainer>

                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <InfoLabelContainer>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <InputTextfield
                          id="card-info-ebay-description"
                          label="eBay Listing Description"
                          value={buildEbayDescription(card, series, selectedVariation)}
                          updateValue={() => {}}
                          readOnly
                          onClick={(e) => e.target.select()}
                          rows={12}
                        />
                      </div>
                      <CopyBtnContainer
                        onClick={() => {
                          handleCopyDescription(selectedVariation);
                        }}
                      >
                        {copiedItem === 'description' ? (
                          <CopyConfirmation>COPIED TO CLIPBOARD!</CopyConfirmation>
                        ) : (
                          <>
                            <img
                              src={copyIcon}
                              alt="Copy"
                              style={{ width: '12px', height: '12px' }}
                            />
                            <span style={{ fontSize: '11px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                              Copy
                            </span>
                          </>
                        )}
                      </CopyBtnContainer>
                    </InfoLabelContainer>
                  </div>
                </DescriptionPreview>
              )}
            </LeftContent>
          </ContentRow>
        </ModalBody>
      </ModalContainer>
    </div>
  );

  return createPortal(modalContent, document.body);
}

